import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronRight,
  FileText,
  Minus,
  Plus,
  RefreshCw,
  ScanLine,
  Search,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { PosBackButton } from '../../components/PosBackButton';
import { Badge } from '../../components/figma-ui';
import { InvoiceModal, type InvoiceSale } from '../../components/figma-extended';
import { formatCurrency } from '../../lib/currency';
import { PaymentMethod, type ProductWithStock } from '../../../domain/models';
import { buildProductSearchHaystack, productSearchHaystackMatches } from '@shared/utils';
import {
  ReceiptStatus,
  type Receipt,
} from './CashierSellingViewModelState';
import { isReceiptRefunded } from './receiptRefundHelpers';
import {
  buildRefundSelection,
  canOfferFullRefund,
  initialLineChoices,
  type RefundLineChoice,
  type RefundMode,
} from './refundSelection';

type RefundStep = 'lookup' | 'review' | 'confirm' | 'done';

const STEPS = ['Recherche', 'Sélection', 'Confirmation', 'Validé'] as const;

const REFUND_METHODS = ['Espèces', 'Carte bancaire', 'Chèque', 'Avoir'] as const;

function paymentLabel(method: PaymentMethod): string {
  switch (method) {
    case PaymentMethod.CARD:
      return 'Carte bancaire';
    case PaymentMethod.CHEQUE:
      return 'Chèque';
    default:
      return 'Espèces';
  }
}

function calcLineTtc(item: Receipt['items'][number]): number {
  const lineHt =
    item.quantity * item.unitPrice * (1 - (item.productDiscountPercentage || 0) / 100);
  return lineHt * (1 + (item.taxRate || 0));
}

function Stepper({ active }: { active: number }) {
  return (
    <div className="ml-2 flex items-center gap-2 flex-wrap">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-1.5">
          <div
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
              i < active
                ? 'bg-emerald-600 text-white'
                : i === active
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-800 text-gray-600'
            }`}
          >
            {i < active ? <Check size={10} /> : i + 1}
          </div>
          <span
            className={`text-[11px] font-medium ${
              i === active ? 'text-amber-300' : i < active ? 'text-gray-500' : 'text-gray-700'
            }`}
          >
            {s}
          </span>
          {i < STEPS.length - 1 && <ChevronRight size={10} className="text-gray-800" />}
        </div>
      ))}
    </div>
  );
}

export interface PosRefundScreenProps {
  receipts: Receipt[];
  selectedReceipt: Receipt | null;
  cashierName: string;
  branchName: string;
  isProcessing: boolean;
  lookupError: string;
  onLoadHistory: () => void;
  onLookup: (receiptId: string) => Promise<Receipt | null>;
  onSelectReceipt: (receipt: Receipt) => void;
  onClear: () => void;
  products?: ProductWithStock[];
  onProcessRefund: (opts: {
    reason?: string;
    receipt?: Receipt;
    items?: Receipt['items'];
    refundAmount?: number;
    collectedAmount?: number;
    exchanges?: Array<{
      originalProductId: string;
      replacementProductId: string;
      replacementName: string;
      quantity: number;
      unitPrice: number;
      taxRate: number;
    }>;
    settled?: boolean;
  }) => Promise<{ refundRef: string } | null>;
  onDone: () => void;
  /** When true, skip lookup if a receipt is already selected (e.g. from history) */
  startAtReview?: boolean;
  /** Session-level refunded receipt ids (persists across PosRefund remounts) */
  refundedIds: Set<string>;
  /** Mark receipt refunded immediately on Confirm click */
  onRefunded: (receiptId: string) => void;
}

export function PosRefundScreen({
  receipts,
  selectedReceipt,
  cashierName,
  branchName,
  isProcessing,
  lookupError,
  onLoadHistory,
  onLookup,
  onSelectReceipt,
  onClear,
  products = [],
  onProcessRefund,
  onDone,
  startAtReview = false,
  refundedIds,
  onRefunded,
}: PosRefundScreenProps) {
  const { t } = useTranslation();
  const [step, setStep] = React.useState<RefundStep>(
    startAtReview && selectedReceipt ? 'review' : 'lookup',
  );
  const [lookupInput, setLookupInput] = React.useState(
    selectedReceipt?.receiptNumber ?? '',
  );
  const [localError, setLocalError] = React.useState('');
  const [receipt, setReceipt] = React.useState<Receipt | null>(
    startAtReview ? selectedReceipt : null,
  );
  const [refundMethod, setRefundMethod] = React.useState(
    selectedReceipt ? paymentLabel(selectedReceipt.paymentMethod) : '',
  );
  const [refundNote, setRefundNote] = React.useState('');
  const [refundRef, setRefundRef] = React.useState('');
  const [invoice, setInvoice] = React.useState<InvoiceSale | null>(null);
  const [lookingUp, setLookingUp] = React.useState(false);
  const [refundMode, setRefundMode] = React.useState<RefundMode>(
    selectedReceipt && !canOfferFullRefund(selectedReceipt) ? 'partial' : 'full',
  );
  const [lines, setLines] = React.useState<RefundLineChoice[]>(
    selectedReceipt ? initialLineChoices(selectedReceipt) : [],
  );
  const [pickerIndex, setPickerIndex] = React.useState<number | null>(null);
  const [pickerQuery, setPickerQuery] = React.useState('');

  const openReceipt = (found: Receipt) => {
    const full = canOfferFullRefund(found);
    setReceipt(found);
    setRefundMethod(paymentLabel(found.paymentMethod));
    setRefundMode(full ? 'full' : 'partial');
    setLines(initialLineChoices(found));
    setPickerIndex(null);
    setPickerQuery('');
  };

  const selection = React.useMemo(
    () => (receipt ? buildRefundSelection(receipt, refundMode, lines) : null),
    [receipt, refundMode, lines],
  );
  const movementAmount = selection
    ? Math.max(selection.refundAmount, selection.collectedAmount)
    : 0;
  const amountCaption = selection && selection.collectedAmount > 0
    ? t('pos.refund.supplement')
    : t('pos.refund.to_refund');

  React.useEffect(() => {
    onLoadHistory();
  }, [onLoadHistory]);

  React.useEffect(() => {
    if (startAtReview && selectedReceipt) {
      if (isReceiptRefunded(selectedReceipt, refundedIds)) {
        setLocalError(
          t('pos.refund.already_refunded_id', { id: selectedReceipt.receiptNumber }),
        );
        setStep('lookup');
        setLookupInput(selectedReceipt.receiptNumber);
        return;
      }
      setReceipt(selectedReceipt);
      setLookupInput(selectedReceipt.receiptNumber);
      openReceipt(selectedReceipt);
      setStep('review');
    }
  }, [startAtReview, selectedReceipt?.id]);

  // Recent list includes refunded rows (disabled) — Figma shows all receipts
  const recentReceipts = receipts.slice(0, 12);
  const displayError = localError || lookupError;

  const fmt = (n: number) => formatCurrency(n, 'TND');

  const doLookup = async () => {
    const id = lookupInput.trim();
    if (!id) {
      setLocalError(t('pos.refund.enter_receipt'));
      return;
    }
    setLookingUp(true);
    setLocalError('');
    try {
      const upper = id.toUpperCase();
      const localHit = receipts.find(
        (r) =>
          r.receiptNumber.toUpperCase() === upper ||
          r.receiptNumber.toUpperCase().includes(upper) ||
          r.id.toUpperCase() === upper,
      );
      if (localHit && isReceiptRefunded(localHit, refundedIds)) {
        setLocalError(
          t('pos.refund.already_refunded_id', { id: localHit.receiptNumber }),
        );
        return;
      }
      if (refundedIds.has(id) || refundedIds.has(upper)) {
        setLocalError(t('pos.refund.already_refunded_id', { id: upper }));
        return;
      }

      const found = await onLookup(id);
      if (!found) {
        setLocalError(t('pos.refund.not_found', { id: lookupInput }));
        return;
      }
      if (isReceiptRefunded(found, refundedIds)) {
        setLocalError(
          t('pos.refund.already_refunded_id', { id: found.receiptNumber }),
        );
        return;
      }
      openReceipt(found);
      setStep('review');
    } finally {
      setLookingUp(false);
    }
  };

  const selectRecent = (r: Receipt) => {
    if (isReceiptRefunded(r, refundedIds)) return;
    onSelectReceipt(r);
    setLookupInput(r.receiptNumber);
    openReceipt(r);
    setLocalError('');
    setStep('review');
  };

  const header = (title: string, subtitle?: string, activeStep = 0, onBack?: () => void) => (
    <div className="px-5 py-4 border-b border-amber-900/40 bg-amber-950/20 flex items-center gap-3 shrink-0">
      <PosBackButton
        onClick={onBack ?? onDone}
        label={t('pos.refund.back')}
      />
      <div className="w-px h-5 bg-amber-900/60" />
      <RefreshCw size={14} className="text-amber-400 shrink-0" />
      <div className="min-w-0">
        <div className="text-amber-300 font-bold text-sm leading-tight truncate">{title}</div>
        {subtitle && <div className="text-[11px] text-gray-500 truncate">{subtitle}</div>}
      </div>
      <Stepper active={activeStep} />
    </div>
  );

  if (step === 'lookup') {
    return (
      <div className="flex-1 flex flex-col bg-gray-950 overflow-hidden">
        {header(t('pos.refund.title'), undefined, 0, () => {
          onClear();
          onDone();
        })}
        <div className="flex-1 overflow-y-auto flex items-start justify-center pt-16 px-8 pb-8">
          <div className="w-full max-w-md space-y-6">
            <div>
              <div className="text-lg font-bold text-white mb-1">{t('pos.refund.search_title')}</div>
              <div className="text-sm text-gray-500">{t('pos.refund.search_hint')}</div>
            </div>
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <ScanLine size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                  <Input
                    value={lookupInput}
                    onChange={(e) => {
                      setLookupInput(e.target.value);
                      setLocalError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void doLookup();
                    }}
                    className={`pl-10 pr-4 py-3.5 bg-gray-800 border text-white text-sm h-auto rounded-xl font-mono ${
                      displayError
                        ? 'border-red-700 focus-visible:ring-red-500/20'
                        : 'border-gray-700 focus-visible:ring-amber-500/20 focus-visible:border-amber-600'
                    }`}
                    placeholder="RCP-20260620-XXXX"
                    autoFocus
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void doLookup()}
                  disabled={lookingUp}
                  className="px-5 py-3 bg-amber-500 hover:bg-amber-400 active:scale-95 text-white font-bold rounded-xl transition-all disabled:opacity-50"
                >
                  {t('pos.refund.search')}
                </button>
              </div>
              {displayError && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertTriangle size={13} />
                  {displayError}
                </div>
              )}
            </div>

            <div>
              <div className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-2">
                {t('pos.refund.recent_title')}
              </div>
              <div className="space-y-2">
                {recentReceipts.length === 0 ? (
                  <div className="text-sm text-gray-600 py-4 text-center">
                    {t('pos.refund.no_recent')}
                  </div>
                ) : (
                  recentReceipts.map((r) => {
                    const alreadyRefunded = isReceiptRefunded(r, refundedIds);
                    if (alreadyRefunded) {
                      return (
                        <div
                          key={r.id}
                          className="w-full flex items-center gap-3 px-4 py-3 bg-gray-900/50 border border-gray-800/50 rounded-xl opacity-50 cursor-not-allowed"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center shrink-0">
                            <FileText size={13} className="text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-mono font-bold text-gray-500 line-through">
                              {r.receiptNumber}
                            </div>
                            <div className="text-xs text-gray-600">
                              {r.createdAt.toLocaleDateString('fr-TN')} ·{' '}
                              {r.createdAt.toLocaleTimeString('fr-TN', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}{' '}
                              · {r.cashierName}
                            </div>
                          </div>
                          <Badge variant={r.status === ReceiptStatus.PARTIAL ? 'warning' : 'error'}>
                            {r.status === ReceiptStatus.PARTIAL
                              ? t('pos.history.partial')
                              : t('pos.history.refunded')}
                          </Badge>
                        </div>
                      );
                    }
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => selectRecent(r)}
                        className="w-full flex items-center gap-3 px-4 py-3 bg-gray-900 border border-gray-800 hover:border-amber-700/50 hover:bg-gray-800/80 rounded-xl text-left transition-all group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-amber-900/30 flex items-center justify-center shrink-0">
                          <FileText size={13} className="text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-mono font-bold text-amber-300">
                            {r.receiptNumber}
                          </div>
                          <div className="text-xs text-gray-500">
                            {r.createdAt.toLocaleDateString('fr-TN')} ·{' '}
                            {r.createdAt.toLocaleTimeString('fr-TN', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}{' '}
                            · {r.cashierName}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-bold tabular-nums text-white">
                            {fmt(r.grandTotal)}
                          </div>
                          <div className="text-[10px] text-gray-600">
                            {r.status === ReceiptStatus.PARTIAL
                              ? t('pos.history.partial')
                              : paymentLabel(r.paymentMethod)}
                          </div>
                        </div>
                        <ChevronRight
                          size={14}
                          className="text-gray-700 group-hover:text-amber-500 transition-colors shrink-0"
                        />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'review' && receipt) {
    return (
      <div className="flex-1 flex flex-col bg-gray-950 overflow-hidden">
        {header(
          `${t('pos.refund.title')} — ${receipt.receiptNumber}`,
          `${receipt.createdAt.toLocaleDateString('fr-TN')} · ${receipt.createdAt.toLocaleTimeString('fr-TN', { hour: '2-digit', minute: '2-digit' })} · ${receipt.cashierName} · ${paymentLabel(receipt.paymentMethod)}`,
          1,
          () => setStep('lookup'),
        )}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div className="flex gap-2">
            {canOfferFullRefund(receipt) && (
              <button
                type="button"
                onClick={() => setRefundMode('full')}
                className={`px-4 py-2 rounded-xl text-sm font-bold border ${
                  refundMode === 'full'
                    ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                    : 'bg-gray-900 border-gray-700 text-gray-400'
                }`}
              >
                {t('pos.refund.full_refund_badge')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setRefundMode('partial')}
              className={`px-4 py-2 rounded-xl text-sm font-bold border ${
                refundMode === 'partial'
                  ? 'bg-teal-500/20 border-teal-500 text-teal-300'
                  : 'bg-gray-900 border-gray-700 text-gray-400'
              }`}
            >
              {t('pos.refund.partial_refund')}
            </button>
          </div>

          {refundMode === 'full' ? (
            <div className="bg-brand/10 border border-brand/30 rounded-xl px-4 py-3 flex items-center gap-2">
              <Check size={13} className="text-brand shrink-0" />
              <span className="text-xs text-teal-300">{t('pos.refund.full_refund_banner')}</span>
            </div>
          ) : (
            <div className="space-y-3">
              {receipt.items.map((item, index) => {
                const max = Math.max(0, item.quantity - (receipt.returnedQuantities?.[index] ?? 0));
                const line = lines[index];
                if (max < 1 || !line) return null;
                const pickerHits = products
                  .filter((product) =>
                    productSearchHaystackMatches(
                      buildProductSearchHaystack([
                        product.name,
                        product.sku,
                        product.barcode,
                        product.description,
                      ]),
                      pickerQuery,
                    ),
                  )
                  .slice(0, 12);
                return (
                  <div key={`${item.productId}-${index}`} className="bg-gray-900 border border-gray-800 rounded-2xl p-4 space-y-3">
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={line.included}
                        onChange={(event) => {
                          const included = event.target.checked;
                          setLines((prev) => prev.map((row, rowIndex) => (
                            rowIndex === index ? { ...row, included } : row
                          )));
                        }}
                        className="mt-1"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-100">{item.productName}</span>
                        <span className="block text-[10px] text-gray-600 font-mono">{item.productId}</span>
                      </span>
                      <span className="ml-auto text-sm font-bold text-amber-300 tabular-nums">{fmt(calcLineTtc(item))}</span>
                    </label>
                    {line.included && (
                      <div className="space-y-3 pl-7">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={line.qty <= 1}
                            onClick={() => setLines((prev) => prev.map((row, rowIndex) => (
                              rowIndex === index ? { ...row, qty: Math.max(1, row.qty - 1) } : row
                            )))}
                            className="w-8 h-8 rounded-lg border border-gray-700 text-gray-300 disabled:opacity-30"
                          >
                            <Minus size={14} className="mx-auto" />
                          </button>
                          <span className="w-8 text-center font-bold tabular-nums text-white">{line.qty}</span>
                          <button
                            type="button"
                            disabled={line.qty >= max}
                            onClick={() => setLines((prev) => prev.map((row, rowIndex) => (
                              rowIndex === index ? { ...row, qty: Math.min(max, row.qty + 1) } : row
                            )))}
                            className="w-8 h-8 rounded-lg border border-gray-700 text-gray-300 disabled:opacity-30"
                          >
                            <Plus size={14} className="mx-auto" />
                          </button>
                          <span className="text-xs text-gray-500">/ {max}</span>
                        </div>
                        <div className="flex gap-2">
                          {(['return', 'exchange'] as const).map((mode) => (
                            <button
                              key={mode}
                              type="button"
                              onClick={() => setLines((prev) => prev.map((row, rowIndex) => (
                                rowIndex === index ? { ...row, mode } : row
                              )))}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${
                                line.mode === mode
                                  ? mode === 'return'
                                    ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                                    : 'bg-teal-500/20 border-teal-500 text-teal-300'
                                  : 'border-gray-700 text-gray-400'
                              }`}
                            >
                              {mode === 'return' ? t('pos.refund.return_line') : t('pos.refund.exchange_line')}
                            </button>
                          ))}
                        </div>
                        {line.mode === 'exchange' && (
                          <div className="space-y-2">
                            <button
                              type="button"
                              onClick={() => {
                                setPickerIndex(pickerIndex === index ? null : index);
                                setPickerQuery('');
                              }}
                              className="text-xs font-semibold text-teal-300"
                            >
                              {line.replacement?.name ?? t('pos.refund.pick_replacement')}
                            </button>
                            {pickerIndex === index && (
                              <div className="space-y-2">
                                <div className="relative">
                                  <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                                  <Input
                                    value={pickerQuery}
                                    onChange={(event) => setPickerQuery(event.target.value)}
                                    className="pl-8 bg-gray-800 border-gray-700 text-white h-9"
                                    placeholder={t('pos.refund.pick_replacement')}
                                  />
                                </div>
                                <div className="max-h-40 overflow-y-auto divide-y divide-gray-800 border border-gray-800 rounded-lg">
                                  {pickerHits.map((product) => (
                                    <button
                                      key={product.id}
                                      type="button"
                                      onClick={() => {
                                        setLines((prev) => prev.map((row, rowIndex) => (
                                          rowIndex === index
                                            ? {
                                                ...row,
                                                replacement: {
                                                  id: product.id,
                                                  name: product.name,
                                                  salePriceHT: product.salePriceHT,
                                                  taxRate: product.taxRate,
                                                },
                                              }
                                            : row
                                        )));
                                        setPickerIndex(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
                                    >
                                      {product.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {selection && (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 flex justify-between items-center">
              <span className="text-base font-bold text-white">{amountCaption}</span>
              <span className="text-2xl font-black tabular-nums text-amber-400">{fmt(movementAmount)}</span>
            </div>
          )}
        </div>

        <div className="px-6 pb-6 pt-3 border-t border-gray-800 shrink-0 flex gap-3">
          <button
            type="button"
            onClick={() => setStep('lookup')}
            className="flex items-center gap-1.5 px-4 py-3 text-gray-400 hover:text-white border border-gray-700 hover:border-gray-600 rounded-xl text-sm font-semibold transition-colors"
          >
            <ArrowLeft size={14} />
            {t('pos.refund.back')}
          </button>
          <button
            type="button"
            disabled={!selection?.canContinue}
            onClick={() => setStep('confirm')}
            className="flex-1 py-3 bg-amber-500 hover:bg-amber-400 disabled:opacity-30 active:scale-[0.99] text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
          >
            {t('pos.refund.continue', { amount: fmt(movementAmount) })}
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  if (step === 'confirm' && receipt) {
    const originalPay = paymentLabel(receipt.paymentMethod);
    return (
      <div className="flex-1 flex flex-col bg-gray-950 overflow-hidden">
        {header(
          `${t('pos.refund.confirm_title')} — ${receipt.receiptNumber}`,
          t('pos.refund.confirm_subtitle'),
          2,
          () => setStep('review'),
        )}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-3">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                {t('pos.refund.original_receipt')}
              </div>
              {(
                [
                  [t('pos.refund.receipt_no'), receipt.receiptNumber],
                  [
                    t('pos.success.date'),
                    `${receipt.createdAt.toLocaleDateString('fr-TN')} · ${receipt.createdAt.toLocaleTimeString('fr-TN', { hour: '2-digit', minute: '2-digit' })}`,
                  ],
                  [t('pos.success.cashier'), receipt.cashierName],
                  [t('pos.success.payment'), originalPay],
                  [t('pos.refund.total_to_return'), fmt(movementAmount)],
                ] as const
              ).map(([k, v]) => (
                <div
                  key={String(k)}
                  className="flex justify-between text-sm border-b border-gray-800 pb-2 last:border-0 last:pb-0"
                >
                  <span className="text-gray-500">{k}</span>
                  <span className="font-semibold text-gray-200 tabular-nums">{v}</span>
                </div>
              ))}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-3">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                {t('pos.refund.refund_method')}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {REFUND_METHODS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setRefundMethod(m)}
                    className={`py-2.5 px-3 rounded-xl text-sm font-semibold border transition-all text-left flex items-center gap-2 ${
                      refundMethod === m
                        ? 'bg-amber-500/15 border-amber-500/50 text-amber-300'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    {refundMethod === m && <Check size={12} className="text-amber-400 shrink-0" />}
                    {m}
                  </button>
                ))}
              </div>
              {refundMethod !== originalPay && refundMethod !== '' && (
                <div className="flex items-start gap-2 bg-amber-900/20 border border-amber-800/40 rounded-xl px-3 py-2.5 mt-2">
                  <AlertTriangle size={12} className="text-amber-400 shrink-0 mt-0.5" />
                  <span className="text-xs text-amber-300">
                    {t('pos.refund.method_mismatch', { original: originalPay })}
                  </span>
                </div>
              )}
              <div className="space-y-1.5 pt-1">
                <div className="text-[11px] text-gray-600 font-semibold uppercase tracking-wide">
                  {t('pos.refund.reason_optional')}
                </div>
                <textarea
                  value={refundNote}
                  onChange={(e) => setRefundNote(e.target.value)}
                  rows={2}
                  placeholder={t('pos.refund.reason_placeholder')}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-600 resize-none"
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                {t('pos.refund.items_refunded')}
              </span>
              <Badge variant="success">
                {refundMode === 'full'
                  ? t('pos.refund.full_refund_badge')
                  : selection?.exchanges.length
                    ? `${t('pos.refund.partial_refund')} + ${t('pos.refund.exchange_line')}`
                    : t('pos.refund.partial_refund')}
              </Badge>
            </div>
            <div className="divide-y divide-gray-800">
              {(selection?.items ?? []).map((l, i) => (
                <div key={`${l.productId}-${i}`} className="px-5 py-3 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-200 truncate">{l.productName}</div>
                    <div className="text-[10px] text-gray-600 font-mono">
                      {l.productId}
                      {l.productDiscountPercentage > 0 && (
                        <span className="ml-2 text-amber-500">−{l.productDiscountPercentage}%</span>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-gray-400 tabular-nums">×{l.quantity}</span>
                  <span className="text-sm tabular-nums text-gray-400 hidden sm:inline">
                    {fmt(l.unitPrice)} HT · {(l.taxRate * 100).toFixed(0)}%
                  </span>
                  <span className="text-sm font-bold tabular-nums text-amber-300 w-24 text-right">
                    {fmt(calcLineTtc(l))}
                  </span>
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-gray-800 flex justify-between items-center bg-gray-800/40">
              <span className="text-base font-bold text-white">{amountCaption}</span>
              <span className="text-2xl font-black tabular-nums text-amber-400">
                {fmt(movementAmount)}
              </span>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 pt-3 border-t border-gray-800 shrink-0 flex flex-col gap-2">
          {(lookupError || localError) && step === 'confirm' && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertTriangle size={13} />
              {lookupError || localError}
            </div>
          )}
          <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setStep('review')}
            className="flex items-center gap-1.5 px-4 py-3 text-gray-400 hover:text-white border border-gray-700 hover:border-gray-600 rounded-xl text-sm font-semibold transition-colors"
          >
            <ArrowLeft size={14} />
            {t('pos.refund.back')}
          </button>
          <button
            type="button"
            disabled={!refundMethod || isProcessing}
            onClick={async () => {
              if (!receipt || !selection) return;
              onSelectReceipt(receipt);
              const reasonParts = [
                refundNote.trim(),
                refundMethod ? `Mode: ${refundMethod}` : '',
              ].filter(Boolean);
              const result = await onProcessRefund({
                reason: reasonParts.join(' · ') || undefined,
                receipt,
                items: selection.items,
                refundAmount: selection.refundAmount,
                collectedAmount: selection.collectedAmount,
                exchanges: selection.exchanges,
                settled: selection.settled,
              });
              if (result) {
                if (selection.settled) {
                  onRefunded(receipt.receiptNumber);
                  onRefunded(receipt.id);
                }
                setRefundRef(result.refundRef);
                setStep('done');
              }
            }}
            className="flex-1 py-3 bg-amber-500 hover:bg-amber-400 disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.99] text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-base"
          >
            <Check size={16} />
            {t('pos.refund.confirm_cta', { amount: fmt(movementAmount) })}
          </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'done' && receipt) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-950 px-8">
        {invoice && <InvoiceModal sale={invoice} onClose={() => setInvoice(null)} />}
        <div className="w-full max-w-md space-y-6 text-center">
          <div className="w-20 h-20 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto">
            <Check size={36} className="text-emerald-400" />
          </div>
          <div>
            <div className="text-2xl font-black text-white">{t('pos.refund.done_title')}</div>
            <div className="text-gray-500 mt-1 text-sm">{t('pos.refund.done_subtitle')}</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 text-left space-y-3">
            {(
              [
                [t('pos.refund.refund_no'), refundRef],
                [t('pos.refund.original_receipt_short'), receipt.receiptNumber],
                [t('pos.refund.amount_refunded'), fmt(movementAmount)],
                [t('pos.refund.mode'), refundMethod],
                [t('pos.refund.type'), refundMode === 'full'
                  ? t('pos.refund.full_refund_badge')
                  : selection?.exchanges.length
                    ? `${t('pos.refund.partial_refund')} + ${t('pos.refund.exchange_line')}`
                    : t('pos.refund.partial_refund')],
                ...(refundNote ? ([[t('pos.refund.reason'), refundNote]] as const) : []),
              ] as const
            ).map(([k, v]) => (
              <div
                key={String(k)}
                className="flex justify-between text-sm border-b border-gray-800 pb-2.5 last:border-0 last:pb-0"
              >
                <span className="text-gray-500">{k}</span>
                <span className="font-semibold text-gray-100 tabular-nums text-right max-w-[55%]">
                  {v}
                </span>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() =>
                setInvoice({
                  id: refundRef,
                  date: receipt.createdAt.toLocaleDateString('fr-TN'),
                  time: receipt.createdAt.toLocaleTimeString('fr-TN'),
                  cashier: cashierName,
                  branch: branchName,
                  paymentMethod: refundMethod,
                  items: receipt.items.map((l) => ({
                    name: l.productName,
                    sku: l.productId,
                    qty: l.quantity,
                    unitPrice: l.unitPrice,
                    tax: l.taxRate * 100,
                    discount: l.productDiscountPercentage,
                  })),
                })
              }
              className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-brand rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <FileText size={14} />
              {t('pos.refund.print_receipt')}
            </button>
            <button
              type="button"
              onClick={() => {
                onClear();
                onDone();
              }}
              className="flex-1 py-3 bg-brand hover:bg-brand-light text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <Check size={14} />
              {t('pos.success.new_sale')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
